import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import { IPlanGroup } from 'types/workout/plan-state';

export type DraftSource = 'ai' | 'manual';
/** @deprecated use DraftSource — alias kept for backward compat */
export type WorkoutDraftSource = DraftSource;

export type DraftKind = 'workout' | 'cycle';

interface BaseDraft {
  id: string;
  kind: DraftKind;
  source: DraftSource;
  /** epoch ms — usado para sort + eviction LRU */
  updatedAt: number;
  createdAt: number;
}

export interface WorkoutDraft extends BaseDraft {
  kind: 'workout';
  planName: string;
  instanceName: string;
  personalizedNotes: string;
  groups: IPlanGroup[];
}

/**
 * Draft del preview de import de ciclo (multi-dia). Sobrevive un refresh
 * accidental durante el parse — evita re-pagar la llamada a OpenAI.
 *
 * `days` mantiene el shape `CycleDayPreview` del hook useImportWorkoutCycleAI.
 * Se serializa tal cual a JSON (IPlanGroup tiene fechas como Date, las
 * convertimos a string en JSON.stringify; al rehidratar quedan como string —
 * los consumers deben normalizar si necesitan Date real).
 */
export interface CycleDraft extends BaseDraft {
  kind: 'cycle';
  cycleName: string;
  durationWeeks: number;
  days: Array<{
    dayNumber: number;
    instanceName: string;
    personalizedNotes: string;
    groups: IPlanGroup[];
  }>;
}

export type Draft = WorkoutDraft | CycleDraft;

export type SaveWorkoutDraftInput = Omit<WorkoutDraft, 'id' | 'updatedAt' | 'createdAt'> & {
  id?: string;
};
export type SaveCycleDraftInput = Omit<CycleDraft, 'id' | 'updatedAt' | 'createdAt'> & {
  id?: string;
};
export type SaveDraftInput = SaveWorkoutDraftInput | SaveCycleDraftInput;

interface DraftWorkoutsState {
  drafts: Draft[];
  /**
   * Guarda un draft. Si `input.id` matchea un draft existente del mismo `kind`,
   * lo actualiza in-place (bump de `updatedAt`). Sino crea uno nuevo.
   *
   * LRU eviction es por `kind`: max 3 workout-drafts + max 3 cycle-drafts.
   * Un cycle draft NO desplaza un workout draft.
   */
  saveDraft: (input: SaveDraftInput) => string;
  deleteDraft: (id: string) => void;
  loadDraft: (id: string) => Draft | undefined;
  /**
   * Lista los drafts filtrados por kind. Equivalente a
   * `drafts.filter(d => d.kind === kind)` pero tipado correctamente.
   */
  listByKind: <K extends DraftKind>(kind: K) => Extract<Draft, { kind: K }>[];
  clearAll: (kind?: DraftKind) => void;
}

const MAX_PER_KIND = 3;
export const DRAFT_WORKOUTS_STORAGE_KEY = 'easetrain-draft-workouts';

export const useDraftWorkoutsStore = create<DraftWorkoutsState>()(
  persist(
    (set, get) => ({
      drafts: [],

      saveDraft: (input: SaveDraftInput) => {
        const now = Date.now();
        const existingId = input.id;
        const existing = existingId
          ? get().drafts.find((d) => d.id === existingId && d.kind === input.kind)
          : undefined;

        if (existing) {
          // Update in-place: mantener id + createdAt, bump updatedAt y campos.
          const updated = {
            ...existing,
            ...input,
            id: existing.id,
            createdAt: existing.createdAt,
            updatedAt: now
          } as Draft;
          set({
            drafts: get().drafts.map((d) => (d.id === existing.id ? updated : d))
          });
          return existing.id;
        }

        const created = {
          ...input,
          id: existingId ?? uuidv4(),
          createdAt: now,
          updatedAt: now
        } as Draft;

        // LRU per kind: evict el mas viejo del MISMO kind si paso el limite.
        const otherKindDrafts = get().drafts.filter((d) => d.kind !== input.kind);
        const sameKindDrafts = [...get().drafts.filter((d) => d.kind === input.kind), created];
        const sortedDesc = [...sameKindDrafts].sort((a, b) => b.updatedAt - a.updatedAt);
        const overLimit = sameKindDrafts.length > MAX_PER_KIND;
        const sameKindKept = overLimit ? sortedDesc.slice(0, MAX_PER_KIND) : sameKindDrafts;

        set({ drafts: [...otherKindDrafts, ...sameKindKept] });
        return created.id;
      },

      deleteDraft: (id: string) => {
        set({ drafts: get().drafts.filter((d) => d.id !== id) });
      },

      loadDraft: (id: string) => {
        return get().drafts.find((d) => d.id === id);
      },

      listByKind: <K extends DraftKind>(kind: K) =>
        get().drafts.filter((d) => d.kind === kind) as Extract<Draft, { kind: K }>[],

      clearAll: (kind?: DraftKind) => {
        if (!kind) {
          set({ drafts: [] });
          return;
        }
        set({ drafts: get().drafts.filter((d) => d.kind !== kind) });
      }
    }),
    {
      name: DRAFT_WORKOUTS_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Solo persistimos los drafts. Nada de funciones / refs.
      partialize: (state) => ({ drafts: state.drafts }),
      // Migracion: drafts antiguos no tenian `kind`. Los marcamos como 'workout'
      // al cargar para que sigan funcionando.
      migrate: (persistedState: unknown, _version) => {
        const state = persistedState as { drafts?: Array<Partial<Draft>> } | null;
        if (!state || !Array.isArray(state.drafts)) return { drafts: [] };
        return {
          drafts: state.drafts.map((d) => (d.kind ? d : { ...d, kind: 'workout' })) as Draft[]
        };
      },
      version: 2
    }
  )
);
